import React from 'react';
import {Avatar, Backdrop, Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography} from "@mui/material";
import {useSession} from "../firebase/UserProvider";
import {login, logout} from "../firebase/auth";
import {saveReturnUrl} from "../shared/loginReturnUrl";
import {demoUsers} from "../shared/DemoUsersLoginPage";
import UserAvatar from "./UserAvatar";

const ORIGINAL_SESSION_KEY = 'dash_original_session';

function saveOriginalSession(user) {
    try {
        const refreshToken = localStorage.getItem('dash_refresh_token');
        if (!refreshToken) return;
        localStorage.setItem(ORIGINAL_SESSION_KEY, JSON.stringify({
            refreshToken,
            email: user.email,
            displayName: user.displayName || user.email,
            uid: user.uid || user.userId,
            hasAvatar: user.hasAvatar === true,
            profilePicture: user.profilePicture || null,
        }));
    } catch {}
}

function loadOriginalSession() {
    try {
        const raw = localStorage.getItem(ORIGINAL_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function clearOriginalSession() {
    try { localStorage.removeItem(ORIGINAL_SESSION_KEY); } catch {}
}

function restoreOriginalSession(originalSession) {
    try {
        localStorage.setItem('dash_refresh_token', originalSession.refreshToken);
        clearOriginalSession();
    } catch {}
}

/**
 * Full-screen overlay shown while impersonating a demo user.
 * Avoids the flash of the login page during the logout→login→reload cycle.
 */
function SwitchingOverlay({user: targetUser}) {
    return (
        <Backdrop
            open
            sx={{
                zIndex: 9999,
                flexDirection: 'column',
                gap: 3,
                bgcolor: 'rgba(15, 23, 42, 0.85)',
                backdropFilter: 'blur(6px)',
            }}
        >
            <Box sx={{position: 'relative', display: 'inline-flex'}}>
                <CircularProgress
                    size={88}
                    thickness={2}
                    sx={{color: 'rgba(255,255,255,0.4)', position: 'absolute', top: -4, left: -4}}
                />
                <Avatar
                    src={targetUser.headshot}
                    alt={targetUser.name}
                    sx={{width: 80, height: 80, fontSize: 32}}
                >
                    {targetUser.name?.[0]}
                </Avatar>
            </Box>

            <Stack alignItems="center" spacing={0.5}>
                <Typography variant="h6" sx={{color: 'white', fontWeight: 600}}>
                    Switching to {targetUser.name}
                </Typography>
                <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.6)'}}>
                    {targetUser.position}
                </Typography>
            </Stack>
        </Backdrop>
    );
}

const RETURN_VALUE = '__return_to_original__';

const AuthSelect = () => {
    const {user, role} = useSession();
    const [selectValue, setSelectValue] = React.useState('');
    const [switching, setSwitching] = React.useState(null); // demo user object while switching
    const [originalSession, setOriginalSession] = React.useState(() => loadOriginalSession());

    React.useEffect(() => {
        setSelectValue(user?.email || '');
    }, [user?.email]);

    const isAdmin = Boolean(
        user?.isAdmin || (Array.isArray(user?.roles) && user.roles.includes('admin'))
    );

    async function handleLogOut() {
        clearOriginalSession();
        try {
            saveReturnUrl();
            await logout();
            window.location.href = window.location.origin + '/?logout';
        } catch (error) {
            console.error("Error signing out:", error);
        }
    }

    const handleSwitch = async (event) => {
        const value = event.target.value;
        if (!value) return;
        if (value === 'signout') {
            await handleLogOut();
            return;
        }

        // Return to original admin session
        if (value === RETURN_VALUE) {
            const saved = loadOriginalSession();
            if (!saved) return;
            restoreOriginalSession(saved);
            setOriginalSession(null);
            window.location.href = window.location.origin + '/';
            return;
        }

        if (user?.email === value) return;

        const selected = demoUsers.find((u) => u.email === value);
        if (!selected) return;

        // Save original admin session before switching so we can return later.
        if (!isDemoAccount) {
            saveOriginalSession(user);
            setOriginalSession(loadOriginalSession());
        }

        // Show impersonation overlay immediately — prevents the login-page flash.
        setSwitching(selected);
        setSelectValue(value);

        try {
            // Login directly without logout first so the session transitions
            // cleanly: new tokens are stored in localStorage, then we do a
            // hard navigation so initFromStorage() restores the new user.
            await login({email: selected.email, password: selected.password});
            window.location.href = window.location.origin + '/';
        } catch (error) {
            console.error('Error switching demo user', error);
            setSwitching(null);
            setSelectValue(user?.email || '');
        }
    };

    const isDemoAccount = Boolean(
        user?.email && demoUsers.some((u) => u.email === user.email)
    );
    const canSwitchDemoUsers = isDemoAccount || isAdmin;

    const roleLabel = role === 'real_user' ? 'Analyst' : (role || '');

    const currentUserMenuItem = user?.email ? (
        <MenuItem key="__current_user__" disabled value={user.email}>
            <Stack direction="row" spacing={1} alignItems="center">
                <UserAvatar user={user} sx={{width: 32, height: 32}}/>
                <Stack>
                    <Typography variant="body1">
                        {user.displayName || user.email || 'User'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {roleLabel}
                    </Typography>
                </Stack>
            </Stack>
        </MenuItem>
    ) : null;

    // "Return to original admin" item shown when browsing as a demo user
    const returnMenuItem = originalSession ? (
        <MenuItem key={RETURN_VALUE} value={RETURN_VALUE}>
            <Stack direction="row" spacing={1} alignItems="center">
                <UserAvatar
                    user={{
                        uid: originalSession.uid,
                        userId: originalSession.uid,
                        displayName: originalSession.displayName,
                        email: originalSession.email,
                        hasAvatar: originalSession.hasAvatar,
                        profilePicture: originalSession.profilePicture,
                    }}
                    sx={{width: 32, height: 32}}
                />
                <Stack>
                    <Typography variant="body1">{originalSession.displayName}</Typography>
                    <Typography variant="body2" color="primary.main">Return to your account</Typography>
                </Stack>
            </Stack>
        </MenuItem>
    ) : null;

    const demoUserMenuItems = demoUsers.map((u) => (
        <MenuItem key={u.email} value={u.email}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar src={u.headshot} sx={{width: 32, height: 32}}/>
                <Stack>
                    <Typography variant="body1">{u.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{u.position}</Typography>
                </Stack>
            </Stack>
        </MenuItem>
    ));

    // While impersonating a demo user show only: "Return to your account" + the current impersonated user.
    // This prevents switching directly between demo users without going back to the parent admin first.
    const isImpersonating = isDemoAccount && Boolean(originalSession);

    // MUI Select requires a flat array — Fragments are not accepted.
    const switchableItems = (() => {
        if (isImpersonating) {
            // Only offer the return-to-parent option; current demo user is shown as the disabled header.
            return [currentUserMenuItem, returnMenuItem].filter(Boolean);
        }
        if (canSwitchDemoUsers) {
            return [returnMenuItem, !isDemoAccount ? currentUserMenuItem : null, ...demoUserMenuItems].filter(Boolean);
        }
        return [currentUserMenuItem].filter(Boolean);
    })();

    return (
        <>
            {switching && <SwitchingOverlay user={switching}/>}

            <FormControl size="small" variant="outlined">
                <Select
                    size="small"
                    value={selectValue}
                    variant="outlined"
                    onChange={handleSwitch}
                    renderValue={(selected) => {
                        if (!selected) return 'Not signed in';
                        const demo = demoUsers.find((u) => u.email === selected);
                        if (demo) {
                            return (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar src={demo.headshot} sx={{width: 24, height: 24}}/>
                                    <span>{demo.name}</span>
                                </Stack>
                            );
                        }
                        return (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <UserAvatar user={user} sx={{width: 24, height: 24}}/>
                                <span>{user?.displayName || user?.email || 'User'}</span>
                            </Stack>
                        );
                    }}
                >
                    {switchableItems}

                    <MenuItem value="signout">
                        <Typography color="error">Sign out</Typography>
                    </MenuItem>
                </Select>
            </FormControl>
        </>
    );
};

export default AuthSelect;
